import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useGSAP } from '@/composables/useGSAP'
import { UIAudio } from '@/config/AudioConfig'
import AudioEngine from '@/webgl/audio/AudioEngine'

import InputRangeCursor from './InputRangeCursor'
import InputRangeItem from './InputRangeItem'

export type InputRangeProps = {
  currentIndex: number
  setCurrentIndex: (index: number) => void
}

export default function InputRange({ currentIndex, setCurrentIndex }: InputRangeProps) {
  const { t } = useTranslation()
  const { gsap, Draggable } = useGSAP()

  const [dragging, setDragging] = useState<boolean>(false)
  const items = useMemo(
    () => [
      {
        value: 0,
        icon: 'range5',
        label: t('questions.likert.5'),
        color: 'text-scarlet',
      },
      {
        value: 1,
        icon: 'range4',
        label: t('questions.likert.4'),
        color: 'text-coral',
      },
      {
        value: 2,
        icon: 'range3',
        label: t('questions.likert.3'),
        color: 'text-dark-blue',
      },
      {
        value: 3,
        icon: 'range2',
        label: t('questions.likert.2'),
        color: 'text-mid-blue',
      },
      {
        value: 4,
        icon: 'range1',
        label: t('questions.likert.1'),
        color: 'text-light-blue',
      },
    ],
    [t]
  )

  const cursor = useRef<HTMLDivElement>(null)
  const container = useRef<HTMLDivElement>(null)
  const [draggable, setDraggable] = useState<Draggable | null>(null)

  const getCurrentIndex = useCallback(
    (x: number): { index: number; x: number } | null => {
      // offsetWidth is 0 while the container is collapsed (e.g. mid exit transition) but Draggable's
      // inertia can still fire; x / 0 gives NaN, which Math.min/max don't clamp.
      const rawIndex = Math.round(x / (container.current.offsetWidth / items.length))
      if (!Number.isFinite(rawIndex)) return null
      const index = Math.max(0, Math.min(items.length - 1, rawIndex))
      const bullet = container.current.querySelector('.InputRange__Bullet') as HTMLElement
      const bulletWidth = bullet.offsetWidth || 0
      const calculatedX = index * ((container.current.offsetWidth - bulletWidth) / (items.length - 1))

      // console.log('[InputRange] getCurrentIndex', { index, bulletWidth, length: items.length, containerW: container.current.offsetWidth, x, calculatedX });

      return {
        index,
        x: calculatedX,
      }
    },
    [items]
  )

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const draggable = Draggable.create(cursor.current, {
        type: 'x',
        bounds: container.current,
        onPress: () => {
          setDragging(true)
          AudioEngine.play(UIAudio.Hover)
        },
        onRelease: () => setDragging(false),
        inertia: true,
        throwResistance: 10000,
        maxDuration: 0.5,
        onDrag: () => {
          const current = getCurrentIndex(draggable.x)
          if (current) setCurrentIndex(current.index)
        },
        onDragEnd: () => {
          AudioEngine.play(UIAudio.Click)
        },
        liveSnap: {
          points: (point) => {
            const current = getCurrentIndex(point.x)
            if (!current) return point
            setCurrentIndex(current.index)
            point.x = current.x
            // console.log('[InputRange] point', point);
            return point
          },
          radius: 20,
        },
      })[0]

      const bullet = container.current.querySelector('.InputRange__Bullet') as HTMLElement
      const bulletWidth = bullet.offsetWidth || 0
      const step = (container.current.offsetWidth - bulletWidth) / (items.length - 1)
      // INITIAL POSITION
      gsap.set(cursor.current, { x: currentIndex * step })
      draggable.update()
      setDraggable(draggable)
    })

    return () => ctx.kill()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gsap, cursor, container, Draggable, setDragging, getCurrentIndex, items, setCurrentIndex])

  useLayoutEffect(() => {
    const onResize = () => {
      if (!container.current || !cursor.current) return
      const bullet = container.current.querySelector('.InputRange__Bullet') as HTMLElement
      const bulletWidth = bullet.offsetWidth || 0
      const step = (container.current.offsetWidth - bulletWidth) / (items.length - 1)
      // INITIAL POSITION
      gsap.set(cursor.current, { x: currentIndex * step })
      draggable.update()
      setDraggable(draggable)
    }
    window.addEventListener('resize', onResize)

    return () => {
      window.removeEventListener('resize', onResize)
    }
  }, [gsap, currentIndex, draggable, items.length])

  const onClickItem = useCallback(
    (value: number) => {
      AudioEngine.play(UIAudio.Click)
      setCurrentIndex(value)

      const bullet = container.current.querySelector('.InputRange__Bullet') as HTMLElement
      const bulletWidth = bullet.offsetWidth || 0
      const step = (container.current.offsetWidth - bulletWidth) / (items.length - 1)

      gsap
        .timeline()
        .add(() => {
          setDragging(true)
        }, 0)
        .to(cursor.current, {
          x: value * step,
          duration: 0.5,
          ease: 'power2.out',
        })
        .add(() => {
          setDragging(false)
        }, 0.5)
      draggable.update()
    },
    [draggable, setCurrentIndex, items, gsap, cursor]
  )

  // const onNext = () => {
  //   setCurrentIndex(currentIndex + 1)
  // }
  // const onPrevious = () => {
  //   setCurrentIndex(currentIndex - 1)
  // }

  return (
    <div
      className='InputRange w-full mx-auto h-160 md:px-40 grid items-center'
      style={{ ['--w']: `calc(100% - 60px)` } as React.CSSProperties}
    >
      <div className='InputRange__Track h-2 bg-dark-cream mx-16 md:mx-80 col-span-1 col-start-1 row-span-1 row-start-1'></div>
      {/* <div
        className="COUCOU bg-scarlet flex ml-[24vw] transition-all duration-333 ease-out-quad md:hidden"
        style={{
          transform: `translateX(-${currentIndex * 50}vw)`,
        }}
      >
        {items.map((item) => (
          <div key={`range-${item.value + 1}`} className="relative w-[50vw] flex items-center justify-center">
            <UIIcon icon={item.icon as UIIconType} className={`text-144 col-start-1 col-span-1 row-start-1 row-span-1 transition-all duration-300 ease-out-quad ${item.color}`} />
            <span className={`absolute top-0 left-0 w-full h-full flex items-center justify-center p-32 text-center text-cream text-body-s transition-opacity duration-500 ease-out-quad`}>{ item.label }</span>
          </div>
        ))}
      </div> */}
      {/* <div className="md:hidden absolute top-1/2 -translate-y-1/2 left-0 z-80">
        {currentIndex > 0 && (
          <IconButton
            onClick={onPrevious}
            icon="previous"
          />
        )}
      </div>
      <div className="md:hidden absolute top-1/2 -translate-y-1/2 right-0 z-80">
        {currentIndex < items.length - 1 && (
          <IconButton
            onClick={onNext}
            icon="next"
          />
        )}
      </div> */}
      <div
        ref={container}
        className='InputRange__Bullets relative col-span-1 col-start-1 row-span-1 row-start-1 flex justify-between'
      >
        {items.map((item, index) => (
          <InputRangeItem
            key={index}
            item={item}
            active={index === currentIndex}
            onClick={onClickItem}
            isFirst={index === 0}
            isLast={index === items.length - 1}
          />
        ))}
        <InputRangeCursor
          ref={cursor}
          className={`absolute top-1/2 -mt-16 md:-mt-80 left-0`}
          dragging={dragging}
          currentIndex={currentIndex}
          currentLabel={items[currentIndex]?.label ?? ''}
        />
      </div>
    </div>
  )
}
